import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'error'
  | 'info'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning';

declare module '@mui/material/styles/createPalette' {
  interface PaletteColor {
    darker: string;
    lighter: string;
  }
  interface SimplePaletteColorOptions {
    darker: string;
    lighter: string;
  }
  interface TypeBackground {
    neutral: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  contrastText: '#fff',
  dark: '#007B55',
  darker: '#005249',
  light: '#5BE584',
  lighter: '#C8FACD',
  main: '#00AB55',
};

const SECONDARY = {
  contrastText: '#fff',
  dark: '#1939B7',
  darker: '#091A7A',
  light: '#84A9FF',
  lighter: '#D6E4FF',
  main: '#3366FF',
};

const INFO = {
  contrastText: '#fff',
  dark: '#006C9C',
  darker: '#003768',
  light: '#61F3F3',
  lighter: '#CAFDF5',
  main: '#00B8D9',
};

const SUCCESS = {
  contrastText: '#fff',
  dark: '#1B806A',
  darker: '#0A5554',
  light: '#86E8AB',
  lighter: '#D8FBDE',
  main: '#36B37E',
};

const WARNING = {
  contrastText: GREY[800],
  dark: '#B76E00',
  darker: '#7A4100',
  light: '#FFD666',
  lighter: '#FFF5CC',
  main: '#FFAB00',
};

const ERROR = {
  contrastText: '#fff',
  dark: '#B71D18',
  darker: '#7A0916',
  light: '#FFAC82',
  lighter: '#FFE9D5',
  main: '#FF5630',
};

const COMMON = {
  action: {
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    disabledOpacity: 0.48,
    focus: alpha(GREY[500], 0.24),
    hover: alpha(GREY[500], 0.08),
    hoverOpacity: 0.08,
    selected: alpha(GREY[500], 0.16),
  },
  common: { black: '#000', white: '#fff' },
  divider: alpha(GREY[500], 0.24),
  error: ERROR,
  grey: GREY,
  info: INFO,
  primary: PRIMARY,
  secondary: SECONDARY,
  success: SUCCESS,
  warning: WARNING,
};

const palette = (themeMode: 'dark' | 'light') => {
  const light = {
    ...COMMON,
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
    background: { default: '#fff', neutral: GREY[200], paper: '#fff' },
    mode: 'light',
    text: {
      disabled: GREY[500],
      primary: GREY[800],
      secondary: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
    background: {
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
      paper: GREY[800],
    },
    mode: 'dark',
    text: {
      disabled: GREY[600],
      primary: '#fff',
      secondary: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
};

export default palette;
